import { useCallback, useEffect } from 'react';

declare global {
    interface Window {
        ReactNativeWebView?: {
            postMessage: (message: string) => void;
        };
    }
}

// 2026-01-09 | dslee | 타입 설명 추가
export type WebViewMessageType =
    | { type: 'INIT' } // 웹뷰에서 지도가 로딩되면 WEB -> RN 으로 전송되는 타입입니다.
    | { type: 'ACTIVATE_POI'; payload: number } // RN -> WEB 으로 특정 POI 를 활성/비활성화 합니다.
    | { type: 'OPEN_POI'; payload: number } // WEB -> RN 으로 특정 장소 바텀시트를 열때 사용합니다.
    | { type: 'CATEGORY_IDX'; payload: number } // RN -> WEB 으로 카테고리 인덱스를 전달합니다.
    | { type: 'DELETE_XID' } // RN -> WEB 삭제 신호를 전송합니다.
    | { type: 'SET_MARKER'; payload: { coordX: number; coordY: number } } // RN -> WEB 으로 설정할 마커 좌표를 전송합니다.
    | { type: 'SHOW_DRAGGABLE_MARKER' } // RN -> WEB 으로 드래그 가능한 마커를 활성화합니다.
    | { type: 'HIDE_DRAGGABLE_MARKER' } // RN -> WEB 으로 드래그 가능한 마커를 비활성화합니다.
    | { type: 'MARKER_DRAGGED'; payload: { coordX: number; coordY: number } } // WEB -> RN 으로 드래그된 마커 좌표를 전송합니다.
    | { type: 'REGION_ZOOM'; payload: { regionCode: string; zoomDirection: string } } // WEB -> RN 으로 활성화된 지역코드와 Zoom In/Out 를 전송합니다. zoomDirection 는 'upper' | 'lower'
    | { type: 'MAP_TOOL', payload: string} // RN -> WEB 으로 지도 툴 타입을 전송합니다.
    | { type: 'HeatMapBlur', payload: number} // RN -> WEB 으로 히트맵 투명도를 전달합니다.
    | { type: 'START_DIRECTION_ANALYSIS'; payload: { duration: number; vehicle: string } } // RN -> WEB 으로 시간별 이동거리 분석 시작 (duration: 분, vehicle: 'car'|'bike'|'foot')
    | { type: 'WEB_VIEW_MESSAGE'; payload: string } // WEB -> RN 으로 메시지를 전송합니다.
    | { type: 'WEB_VIEW_LOADING_STATE'; payload: { isLoading: boolean; } } // WEB -> RN 으로 로딩 상태를 전송합니다.
    | { type: 'RESET_DIRECTION_ANALYSIS_MARKER'; } // RN -> WEB 으로 마커를 다시 설정할 수 있도록 초기화 신호를 전송합니다.
    | { type: 'DIRECTION_MARKER_STATUS'; payload: { hasMarker: number[] } } // WEB -> RN 으로 시간별 이동거리 분석 마커 정보를 전송합니다.
    | { type: 'DIRECTION_ANALYSIS_COMPLETED'; payload: { isCompleted: boolean } } // WEB -> RN 으로 시간별 이동거리 분석 완료 여부를 전송합니다.
    | { type: 'START_ACCESSIBILITY_ANALYSIS'; } // RN -> WEB 으로 접근성 분석 시작 신호를 전송합니다
    | { type: 'RESET_ACCESSIBILITY_ANALYSIS'; } // RN -> WEB 으로 접근성 분석 초기화 신호를 전송합니다
    | { type: 'REFRESH_MAP'; } // RN -> WEB 으로 지도 새로고침 신호를 전송합니다.
    | { type: 'CENTER_MY_LOCATION'; payload: { latitude: number; longitude: number }; }; // RN -> WEB 지도를 현재 위치로 이동 요청합니다.


export function useWebViewBridge(onWebMessage: (message: WebViewMessageType) => void) {
    // Web -> RN 메시지 전송 (웹에서 RN으로)
    const postMessage = useCallback((message: WebViewMessageType) => {
        if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === 'function') {
            window.ReactNativeWebView.postMessage(JSON.stringify(message));
        }
    }, []);

    // RN -> Web 메시지 수신 (RN에서 웹으로)
    const onMessage = useCallback(
        (event: MessageEvent) => {
            try {
                const message = JSON.parse(event.data);
                onWebMessage?.(message);
            } catch (e) {
                console.warn('Invalid WebView message:', event.data);
            }
        },
        [onWebMessage]
    );

    // 메시지 리스너 등록
    // Android: document, iOS: window 로 수신
    useEffect(() => {
        document.addEventListener('message', onMessage as any);
        window.addEventListener('message', onMessage as any);
        return () => {
            document.removeEventListener('message', onMessage as any);
            window.removeEventListener('message', onMessage as any);
        };
    }, [onMessage]);

    return { postMessage };
}
